import { api, unwrap } from '../../shared/api/client';
import { tokenStore } from '../../shared/api/token-store';
import type { components } from '../../shared/api/schema.gen';

export type TAuthResult = components['schemas']['AuthResultResDto'];
export type TUser = components['schemas']['UserResDto'];
export type TInvitationPreview = components['schemas']['InvitationLookupResDto'];

const keep = (result: TAuthResult): TAuthResult => {
  tokenStore.set(result.tokens.accessToken, result.tokens.refreshToken);
  return result;
};

export const login = async (body: components['schemas']['LoginReqDto']): Promise<TAuthResult> =>
  keep(await unwrap(api.POST('/v1/auth/login', { body })));

export const register = async (
  body: components['schemas']['RegisterReqDto'],
): Promise<TAuthResult> => keep(await unwrap(api.POST('/v1/auth/register', { body })));

export const forgotPassword = async (
  body: components['schemas']['ForgotPasswordReqDto'],
): Promise<void> => {
  await unwrap(api.POST('/v1/auth/forgot-password', { body }));
};

export const resetPassword = async (
  body: components['schemas']['ResetPasswordReqDto'],
): Promise<void> => {
  await unwrap(api.POST('/v1/auth/reset-password', { body }));
};

export const lookupInvitation = (token: string): Promise<TInvitationPreview> =>
  unwrap(api.GET('/v1/invitations/lookup', { params: { query: { token } } }));

export const acceptInvitation = async (
  body: components['schemas']['AcceptInvitationReqDto'],
): Promise<TAuthResult> => keep(await unwrap(api.POST('/v1/invitations/accept', { body })));

export const signOut = (): void => tokenStore.clear();

export type TMembership = components['schemas']['MembershipResDto'];
export type TPreferences = components['schemas']['PreferencesResDto'];

export const me = (): Promise<TUser> => unwrap(api.GET('/v1/users/me'));

export const myOrganizations = (): Promise<TMembership[]> =>
  unwrap(api.GET('/v1/users/me/organizations'));

export const myPreferences = (): Promise<TPreferences> =>
  unwrap(api.GET('/v1/users/me/preferences'));

export const updatePreferences = (
  body: components['schemas']['UpdatePreferencesReqDto'],
): Promise<TPreferences> => unwrap(api.PATCH('/v1/users/me/preferences', { body }));

export const switchOrganization = async (organizationId: string): Promise<TAuthResult> =>
  keep(await unwrap(api.POST('/v1/auth/organization', { body: { organizationId } })));

// Out of an organization just left or deleted. Its tokens may already be refused, so any
// failure signs out: signing in again always lands somewhere valid.
export const moveOutOf = async (organizationId: string): Promise<'switched' | 'signed-out'> => {
  const next = await myOrganizations().then(
    (all) => all.find((entry) => entry.organizationId !== organizationId),
    () => undefined,
  );
  if (next) {
    const switched = await switchOrganization(next.organizationId).then(
      () => true,
      () => false,
    );
    if (switched) return 'switched';
  }
  signOut();
  return 'signed-out';
};

export type TResetToken = components['schemas']['ResetTokenResDto'];

export const describeResetToken = (token: string): Promise<TResetToken> =>
  unwrap(api.GET('/v1/auth/reset-password', { params: { query: { token } } }));

export const confirmEmail = async (token: string): Promise<void> => {
  await unwrap(api.POST('/v1/auth/confirm', { body: { token } }));
};
